/**
 * Auto-demo script — single source of truth for:
 *  - the React orchestrator (imported by lib/auto-demo.tsx)
 *  - the Node TTS generator (scripts/generate-audio.mts)
 *
 * Narration is English only. Each step has:
 *  - id        used as the mp3 filename (e.g. 01-intro.mp3)
 *  - narration text passed to OpenAI TTS
 *  - scrollTo  CSS selector scrolled into view before the step begins
 *  - highlight CSS selector that receives .auto-demo-highlight while active
 *  - action    optional command dispatched into L2Demo / L3Demo
 */

import type { ConditionState } from '../types'

export type AutoDemoAction =
  | { target: 'l2-tab'; value: 'rag' | 'mcp' | 'skill' }
  | { target: 'l3-goal'; value: 'compliance' | 'uptime' | 'cost' }
  | { target: 'l3-conditions'; value: ConditionState }
  | { target: 'l3-node'; value: string }
  | { target: 'l3-reset' }

export interface AutoDemoStep {
  id: string
  narration: string
  scrollTo?: string
  highlight?: string
  action?: AutoDemoAction
}

const HARSH_CONDITIONS: ConditionState = {
  H2S_pct: 14.8,
  Cl_mg_L: 22000,
  vapor_pressure_ratio: 0.52,
}

const MILD_CONDITIONS: ConditionState = {
  H2S_pct: 0.2,
  Cl_mg_L: 3000,
  vapor_pressure_ratio: 0.18,
}

export const AUTO_DEMO_SCRIPT: AutoDemoStep[] = [
  {
    id: '01-intro',
    narration:
      'Welcome to the Orion AI three-stage demo. We will walk through how enterprise AI evolves — from a generic language model, to the modern enterprise stack, to a system anchored on ontology. Every example uses the same real-world scenario: an emergency mechanical-seal procurement at an onshore gas plant.',
    scrollTo: '[data-demo-target="hero"]',
    highlight: '[data-demo-target="hero-story"]',
  },
  {
    id: '02-l1-overview',
    narration:
      'Stage one, L1, is a generic language model. The procurement lead asks which of three seals to award. The model answers with API 682 textbook knowledge — it talks about plan selection, sulfide resistance, certificate timeliness — but it never picks a winner, because it has no access to the enterprise data needed to decide.',
    scrollTo: '#L1',
    highlight: '[data-demo-target="l1-chat"]',
  },
  {
    id: '03-l2-rag',
    narration:
      'Stage two, L2, is the enterprise AI stack — three pieces bolted onto the language model. The first piece is RAG: retrieval-augmented generation. The AI now cites three real enterprise documents — the pump datasheet, the NACE standard, and a procurement policy. It can read your library, but still cannot act.',
    scrollTo: '#L2',
    highlight: '[data-demo-target="l2-rag-citations"]',
    action: { target: 'l2-tab', value: 'rag' },
  },
  {
    id: '04-l2-mcp',
    narration:
      'Second piece: MCP, the model context protocol. The AI calls live enterprise systems — EAM for the asset baseline, SAP for the RFQ, the supplier approval list, the quality management system, and PI for measured operating data. From those calls a compliance matrix is built automatically.',
    scrollTo: '#L2',
    highlight: '[data-demo-target="l2-mcp-stack"]',
    action: { target: 'l2-tab', value: 'mcp' },
  },
  {
    id: '05-l2-skill',
    narration:
      'Third piece: Skill. A predefined procedure runs end-to-end — extract baseline, pull bids, compare against the datasheet, query the approved supplier list, check certificate validity, evaluate the change-of-management trigger, retrieve historical claims, and generate a final report ready for sign-off.',
    scrollTo: '#L2',
    highlight: '[data-demo-target="l2-skill-steps"]',
    action: { target: 'l2-tab', value: 'skill' },
  },
  {
    id: '06-l2-limit',
    narration:
      'Yet the three pieces do not share semantics. Each Skill hardcodes one path; the world outside the RFQ stays invisible; a threshold change has to be applied skill by skill. Change the goal and the whole stack has to be rebuilt. This is the shared ceiling of stage two.',
    scrollTo: '#L2',
    highlight: '[data-demo-target="l2-limitation"]',
  },
  {
    id: '07-l3-intro',
    narration:
      'Stage three, L3, is AI plus ontology. The AI no longer executes pre-written procedures. It reads a semantic foundation — objects, relations, constraints, actions — and synthesizes the execution path live, every single time.',
    scrollTo: '#L3',
    highlight: '[data-demo-target="l3-whiteboard"]',
    action: { target: 'l3-reset' },
  },
  {
    id: '08-l3-goal-compliance',
    narration:
      'Start with the "Compliance-first" goal. From the goal node, the ontology dereferences the pump, runs an operating-condition inference, fires the NACE material constraint and the API plan constraint, evaluates candidates, finds an additional supplier through cross-RFQ coverage, and lands on an expand-and-raise-MOC action.',
    scrollTo: '#L3',
    highlight: '[data-demo-target="l3-whiteboard"]',
    action: { target: 'l3-goal', value: 'compliance' },
  },
  {
    id: '09-l3-goal-uptime',
    narration:
      'Switch to "No-stop". Same ontology, completely different chain. Now the path traces from the standby pump and live PI data through a remaining-life estimate, hits a backup-SLA constraint, generates a bridge plan, and concludes with intensified inspection plus a load reduction.',
    scrollTo: '#L3',
    highlight: '[data-demo-target="l3-whiteboard"]',
    action: { target: 'l3-goal', value: 'uptime' },
  },
  {
    id: '10-l3-goal-cost',
    narration:
      'Switch to "Minimum total cost". The ontology pulls in a cost model and a risk model, runs a ten-thousand-iteration Monte Carlo against the current conditions, evaluates the mandatory MOC rule, ranks candidates, and recommends the cost-optimal action — including accident probability and process cost.',
    scrollTo: '#L3',
    highlight: '[data-demo-target="l3-whiteboard"]',
    action: { target: 'l3-goal', value: 'cost' },
  },
  {
    id: '11-l3-conditions-harsh',
    narration:
      'Now move the operating-condition sliders. Push H₂S, chloride, and vapor-pressure ratio up. The workflow re-synthesizes immediately — more constraints fire, the candidate set shrinks, the recommended action shifts toward expanding the RFQ and raising a change of management.',
    scrollTo: '#L3',
    highlight: '[data-demo-target="l3-sliders"]',
    action: { target: 'l3-conditions', value: HARSH_CONDITIONS },
  },
  {
    id: '12-l3-conditions-mild',
    narration:
      'Drop the conditions to a mild service. The hard constraints disappear, the path collapses, and the action becomes a simple direct award. Nothing about this workflow is pre-written — it is synthesized from goal plus conditions, every single time.',
    scrollTo: '#L3',
    highlight: '[data-demo-target="l3-sliders"]',
    action: { target: 'l3-conditions', value: MILD_CONDITIONS },
  },
  {
    id: '13-l3-attribution',
    narration:
      'Finally, click any node — for example the final action — and reverse attribution lights up every ancestor that contributed to it: the goal, the objects, the inferences, the constraints, the data sources. Any AI decision can be traced back through the graph. This is governance, not guesswork.',
    scrollTo: '#L3',
    highlight: '[data-demo-target="l3-whiteboard"]',
    action: { target: 'l3-node', value: 'g3-act' },
  },
  {
    id: '14-outro',
    narration:
      'That is the three-stage map. Ontology is not another bolt-on capability — it gives enterprise AI a shared semantic foundation, where every path is synthesized on demand, traceable, and governable. Thanks for watching.',
    scrollTo: '#L3',
    highlight: '[data-demo-target="l3-advantages"]',
    action: { target: 'l3-reset' },
  },
]
